<template>
  <div v-if="item?.type === 'file'" class="p-2 content-grid-file-item flex flex-col items-center">
    <div class="relative flex">
      <CImage
        v-if="hasValidThumbnail"
        :ai-img-description="item.file?.name"
        class="content-grid-file-item__img rounded-2 overflow-hidden"
        height="135"
        :img-props="{ draggable: false }"
        :lazy="isDefaultDisplayMode"
        object-fit="cover"
        preview-disabled
        :src="currentThumbnailUrl"
        width="240"
        @error="handleImageError"
        @touchend="handleSwipe"
        @touchmove="handleTouchMove"
        @touchstart="handleTouchStart"
      />
      <!-- DEFAULT THUMBNAILS -->
      <div v-else class="content-grid-file-item__img rounded-2 overflow-hidden w-[240px] h-[135px]">
        <img
          v-if="defaultThumbnailUrl"
          :alt="item.file?.name || 'File thumbnail'"
          class="w-full h-full object-cover"
          :src="defaultThumbnailUrl"
          @error="onThumbnailError"
        />
        <div v-else class="bg-primary6 w-full h-full flex items-center justify-center">
          <CIcon :color="themeVars.primary3" fa-type="fas" icon="file" size="48px" />
        </div>
      </div>

      <div
        v-if="item.file.content_type === FileContentTypeEnum.PDF && item.file.content_thumbnails?.length"
        class="absolute bottom-2 left-0 right-0 flex justify-center"
      >
        <div
          v-if="showPreviewThumbnails && item.file.content_thumbnails?.length > 1"
          class="flex dot-slider gap-2 pa-2 justify-center rounded-2"
          @click.stop
        >
          <div
            v-for="thumbnail in item.file.content_thumbnails?.slice(0, MAX_THUMBNAILS)"
            :key="thumbnail"
            class="dot w-[10px] h-[10px] rounded-full cursor-pointer"
            :class="{ 'dot--active': currentThumbnail === thumbnail }"
            @click.stop="changeActiveThumbnail(thumbnail)"
          />
        </div>
      </div>
    </div>

    <div class="font-700 text-truncate w-full mt-2">
      {{ item.file.name }}
    </div>

    <div class="flex items-center justify-between description text-truncate w-full min-h-8">
      <span v-if="item.file.content_type === FileContentTypeEnum.AR && isPitcherOrIosDevice">
        <CIcon :icon="ComponentIcon.AR" size="12px" />
        {{ t('canvasUI.canvasBuilder.content.viewInAr') }}
      </span>
      <span v-else>
        <component :is="renderContentType(item.file.content_type)" v-if="item.file.content_type" />
      </span>
      <div class="flex gap-2">
        <CPopularityAndEngagementIcons
          v-if="launchDarkly.show_popularity_icons && isEditMode"
          class="flex gap-2"
          :engagement="launchDarkly.show_popularity_icons ? popularityDict[item.file.id]?.engagement : undefined"
          :is-recommended="launchDarkly.show_popularity_icons ? recommendedSectionIds.includes(item.file.id) : false"
          :popularity="launchDarkly.show_popularity_icons ? popularityDict[item.file.id]?.popularity : undefined"
          :views="launchDarkly.show_popularity_icons ? (popularityDict[item.file.id]?.views ?? 0) : 0"
        />
        <CInfoBadge
          v-if="isExpired && isAdmin"
          v-tooltip="
            t('canvasUI.files.expiredTooltip', {
              time: dayjsInstance(expiresAt).format('HH:mm'),
              date: dayjsInstance(expiresAt).format('LL'),
            })
          "
          :background-color="themeVars.error2"
          :color="themeVars.error"
          fa-type="fa-kit"
          icon="regular-clock-circle-exclamation"
          variation="minimal"
        />
        <CInfoBadge
          v-if="isExpiring && !isExpired && isAdmin"
          v-tooltip="
            t('canvasUI.files.expiringTooltip', {
              time: dayjsInstance(expiresAt).format('HH:mm'),
              date: dayjsInstance(expiresAt).format('LL'),
            })
          "
          :background-color="themeVars.warning2"
          :color="themeVars.warning3"
          fa-type="fa-kit"
          icon="regular-clock-circle-exclamation"
          variation="minimal"
        />
        <CInfoBadge
          v-if="!isShareable"
          v-tooltip="t('canvasUI.components.ContentGrid.sharingDisabled')"
          :background-color="themeVars.error2"
          :color="themeVars.error"
          fa-type="fa-kit"
          icon="solid-share-from-square-slash"
          variation="minimal"
        />
      </div>
    </div>
    <div
      v-if="mode === CanvasBuilderMode.SHARING && isDefaultDisplayMode && canDownload"
      class="pt-1 flex justify-end w-full"
    >
      <DownloadButton v-tooltip="t('canvasUI.components.ContentGrid.downloadFile')" @click="downloadFile()" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, inject, watch, type ComputedRef } from 'vue'
import { useI18n } from 'vue-i18n'

import CIcon from '@lib/components/CIcon/CIcon.vue'
import CImage from '@lib/components/CImage/CImage.vue'
import CInfoBadge from '@lib/components/CInfoBadge/CInfoBadge.vue'
import CPopularityAndEngagementIcons from '@lib/components/CPopularityAndEngagementIcons/CPopularityAndEngagementIcons.vue'
import DownloadButton from '@canvas-builder/components/container/DownloadButton/DownloadButton.vue'
import { getDefaultThumbnailUrl, onThumbnailError as handleThumbnailError } from '@lib/utils/defaultThumbnail.util'
import tooltipDirective from '@lib/directives/tooltip'
import useFileDisplayHelpers from '@lib/composables/useFileDisplayHelpers'
import useRecommendations from '@canvas-builder/composables/useRecommendations'
import useThemeVars from '@lib/composables/useThemeVars'
import useImageAccessibility from '@lib/composables/useImageAccessibility'
import { FileContentTypeEnum } from '@openapi'
import type { ContentGridProps, ContentGridFileProps } from '@canvas-builder/types/canvas'
import { CanvasBuilderMode, ComponentIcon } from '@canvas-builder/types/canvas'
import useCanvas from '@canvas-builder/composables/useCanvas'
import useSectionPopularity from '@canvas-builder/composables/useSectionPopularity'
import { isPitcherOrIosDevice } from '@lib/util/userAgents'
import type { LaunchDarklyEnv } from '@lib/types/launchDarkly.types'
import dayjsInstance from 'dayjs'

const props = withDefaults(
  defineProps<{
    item: ContentGridProps['items'][number]
    showPreviewThumbnails?: boolean
    allowDownload?: boolean
  }>(),
  {
    showPreviewThumbnails: false,
    allowDownload: true,
  },
)

const launchDarkly = inject<ComputedRef<LaunchDarklyEnv>>(
  'launchDarkly',
  computed(() => ({})),
)

const MAX_THUMBNAILS = 4

const { t } = useI18n()
const { mode, isEditMode, onDownloadFile, isDefaultDisplayMode, isAdmin, canvasMetadata } = useCanvas()
const { renderContentType, isFileExpired, isFileExpiring } = useFileDisplayHelpers()
const { recommendedSectionIds } = useRecommendations()
const { popularityDict } = useSectionPopularity(computed(() => ({}) as Record<string, any>))
const themeVars = useThemeVars()
const vTooltip = tooltipDirective

const item = computed(() => (props.item.type === 'file' ? props.item : null))

const isShareable = computed(() => item.value?.file.permissions?.can_share !== false)

const isAdvancedSharingEnabled = computed(() => !!launchDarkly.value?.use_advanced_sharing_modal)
const canDownload = computed(() => {
  // First check if the file has explicit download permission (file-level)
  if (item.value?.file.permissions?.can_download === false) {
    return false
  }
  // Then check canvas-level allowDownload setting from metadata
  const canvasAllowDownload = canvasMetadata.value?.allow_download
  if (isAdvancedSharingEnabled.value && canvasAllowDownload === false) {
    return false
  }
  // Finally check the prop passed to this component
  return props.allowDownload
})

const expiresAt = computed(() => item.value?.file.expires_at || '')

const isExpired = computed(() => isFileExpired(expiresAt.value))

const isExpiring = computed(() => isFileExpiring(expiresAt.value))

const currentThumbnail = ref<string | null>(
  !item.value?.file?.thumbnail_url && item.value?.file?.content_thumbnails?.[0]
    ? item.value?.file?.content_thumbnails?.[0]
    : null,
)
const touchStartX = ref(0)
const touchEndX = ref(0)

const currentThumbnailUrl = computed(() => {
  if (currentThumbnail.value) {
    return currentThumbnail.value
  }

  if (item.value?.file?.thumbnail_url) {
    return item.value.file.thumbnail_url
  }

  if (item.value?.file?.content_thumbnails?.[0]) {
    return item.value.file.content_thumbnails[0]
  }

  return null
})

const { hasValidThumbnail, handleImageError } = useImageAccessibility({
  thumbnailUrl: currentThumbnailUrl,
  hasAIEnabled: computed(() => !!launchDarkly.value.enable_ai_generated_thumbnails),
  hasFile: computed(() => !!item.value?.file),
})

const defaultThumbnailUrl = computed(() => {
  const file = item.value?.file
  if (!file) return null
  return getDefaultThumbnailUrl(file as any)
})

const onThumbnailError = handleThumbnailError

function handleTouchStart(e: TouchEvent) {
  touchStartX.value = e.touches[0].clientX
}

function handleTouchMove(e: TouchEvent) {
  touchEndX.value = e.touches[0].clientX
}

function handleSwipe() {
  if (!currentThumbnail.value || !props.showPreviewThumbnails) return

  const difference = touchEndX.value - touchStartX.value
  const thumbnails = item.value?.file.content_thumbnails || []
  let currentIndex = thumbnails.indexOf(currentThumbnail.value)
  if (difference > 50) {
    // swipe right
    currentIndex = (currentIndex - 1 + thumbnails.length) % thumbnails.length
  } else if (difference < -50) {
    // swipe left
    currentIndex = (currentIndex + 1) % thumbnails.length
  }
  currentThumbnail.value = thumbnails[currentIndex]
}

const downloadFile = () => {
  item.value && onDownloadFile.value?.(item.value.file)
}

const changeActiveThumbnail = (thumbnail: NonNullable<ContentGridFileProps['content_thumbnails']>[number]) => {
  currentThumbnail.value = thumbnail
}

watch(
  () => props.item,
  (newItem, oldItem) => {
    if (newItem && newItem.type === 'file' && newItem !== oldItem) {
      currentThumbnail.value =
        !newItem.file?.thumbnail_url && newItem.file?.content_thumbnails?.[0]
          ? newItem.file?.content_thumbnails?.[0]
          : null
    }
  },
  { deep: true },
)

// Watch for thumbnail changes
watch(
  () => item.value?.file?.thumbnail_url,
  () => {
    currentThumbnail.value = null
  },
)
</script>

<style scoped>
.content-grid-file-item {
  color: 1px solid v-bind('themeVars.text');
}
.content-grid-file-item__img {
  outline: 1px solid v-bind('themeVars.primary6');
}

.description {
  color: v-bind('themeVars.text3');
}

.dot-slider {
  .dot {
    border-radius: 50%;
    background-color: rgba(0, 0, 0, 0.3);
    border: 1px solid rgba(255, 255, 255, 0.6);
    transition:
      box-shadow 0.3s 0,
      background-color 0.3s 0;

    &.dot--active {
      height: 10px;
      width: 10px;
      background-color: v-bind('themeVars.primary');
    }
  }
}
</style>
